import { Link, Navigate } from "@tanstack/react-router";
import type {
  MeResponse,
  NextAction,
  WorkQueueItem,
  WorkQueueResponse,
  WorkQueueScope,
} from "@tex/finance-shared";
import { Alert, Mono, Select } from "../components/ui";
import { errorMessage } from "../lib/api";
import { formatAmount, formatDate, humanize } from "../lib/format";
import { NEXT_ACTION_LABELS, REVIEW_STATUS_LABELS } from "../lib/review-labels";
import { useAssignCase, useWorkQueue } from "../lib/reviews";
import { isTexStaff, useMe } from "../lib/session";

const SCOPES: { value: WorkQueueScope; label: string }[] = [
  { value: "mine", label: "Mine" },
  { value: "unassigned", label: "Unassigned" },
  { value: "all", label: "All in review" },
];

const NEXT_ACTION_TONE: Record<NextAction, string> = {
  start_review: "bg-sky-100 text-sky-900",
  read_response: "bg-violet-100 text-violet-900",
  write_memo: "bg-neutral-100 text-neutral-800",
  resolve_flags: "bg-amber-100 text-amber-900",
  awaiting_information: "bg-neutral-100 text-neutral-500",
  specialist_review: "bg-amber-100 text-amber-900",
  ready_to_freeze: "bg-green-100 text-green-900",
  frozen_awaiting_package: "bg-green-100 text-green-900",
  not_proceeding: "bg-red-100 text-red-900",
};

export function NextActionBadge({ action }: { action: NextAction }) {
  return (
    <span
      className={`inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium ${NEXT_ACTION_TONE[action]}`}
    >
      {NEXT_ACTION_LABELS[action]}
    </span>
  );
}

export function TexQueuePage({
  scope,
  onScope,
}: {
  scope: WorkQueueScope;
  onScope: (s: WorkQueueScope) => void;
}) {
  const me = useMe();
  const staff = isTexStaff(me.data);
  const queue = useWorkQueue(scope, staff);

  if (me.data === null) return <Navigate to="/sign-in" />;
  if (!me.data) return <p className="text-sm text-neutral-500">Loading…</p>;
  if (!staff)
    return (
      <Alert tone="error">The underwriting queue is for Tex staff only.</Alert>
    );

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-neutral-900">Underwriting queue</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Submitted applications waiting on Tex, oldest first. Nothing here is
            a credit decision.
          </p>
        </div>
        <div
          role="tablist"
          className="flex gap-1 rounded-md border border-[var(--border-subtle)] bg-white p-0.5 text-sm"
        >
          {SCOPES.map((s) => (
            <button
              key={s.value}
              role="tab"
              type="button"
              aria-selected={scope === s.value}
              onClick={() => onScope(s.value)}
              className={`rounded px-3 py-1 ${scope === s.value ? "bg-neutral-900 text-white" : "text-neutral-500 hover:bg-neutral-100"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {queue.isError && <Alert tone="error">{errorMessage(queue.error)}</Alert>}
      {queue.isPending && (
        <p className="text-sm text-neutral-500">Loading queue…</p>
      )}
      {queue.data && <QueueTable me={me.data} data={queue.data} />}
    </section>
  );
}

function QueueTable({ me, data }: { me: MeResponse; data: WorkQueueResponse }) {
  if (data.items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-neutral-300 bg-white p-8 text-center text-sm text-neutral-500">
        Nothing waiting in this view.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-[var(--border-subtle)] bg-white shadow-sm">
      <table className="w-full text-sm">
        <thead className="border-b border-neutral-100 bg-neutral-50/60 text-left font-display text-[11px] font-bold uppercase tracking-wider text-neutral-500">
          <tr>
            <th className="px-3 py-2">Request</th>
            <th className="px-3 py-2">Buyer · Dealer</th>
            <th className="px-3 py-2 text-right">Requested</th>
            <th className="px-3 py-2">Waiting since</th>
            <th className="px-3 py-2">Reviewer</th>
            <th className="px-3 py-2">Review</th>
            <th className="px-3 py-2">Next</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((item) => (
            <QueueRow
              key={item.case_id}
              me={me}
              item={item}
              reviewers={data.reviewers}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AssignControl({
  me,
  caseId,
  assigned,
  reviewers,
}: {
  me: MeResponse;
  caseId: string;
  assigned: WorkQueueItem["assigned_reviewer"];
  reviewers: WorkQueueResponse["reviewers"];
}) {
  const assign = useAssignCase(caseId);
  const mine = assigned?.user_id === me.user.id;
  if (me.tex_role === "ops_admin") {
    return (
      <div className="space-y-1">
        <Select
          aria-label="Assigned reviewer"
          value={assigned?.user_id ?? ""}
          disabled={assign.isPending}
          onChange={(e) => assign.mutate(e.target.value || null)}
          className="!w-auto !py-0.5 text-xs"
        >
          <option value="">Unassigned</option>
          {reviewers.map((r) => (
            <option key={r.user_id} value={r.user_id}>
              {r.name ?? r.email}
              {r.user_id === me.user.id ? " (me)" : ""}
            </option>
          ))}
        </Select>
        {assign.isError && (
          <p className="text-xs text-red-700">{errorMessage(assign.error)}</p>
        )}
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-xs text-neutral-700">
          {assigned
            ? mine
              ? "You"
              : (assigned.name ?? assigned.email)
            : "Unassigned"}
        </span>
        {assigned === null && (
          <button
            type="button"
            className="text-xs text-tex-blue hover:underline"
            disabled={assign.isPending}
            onClick={() => assign.mutate(me.user.id)}
          >
            Take
          </button>
        )}
        {mine && (
          <button
            type="button"
            className="text-xs text-neutral-500 hover:underline"
            disabled={assign.isPending}
            onClick={() => assign.mutate(null)}
          >
            Release
          </button>
        )}
      </div>
      {assign.isError && (
        <p className="text-xs text-red-700">{errorMessage(assign.error)}</p>
      )}
    </div>
  );
}

function QueueRow({
  me,
  item,
  reviewers,
}: {
  me: MeResponse;
  item: WorkQueueItem;
  reviewers: WorkQueueResponse["reviewers"];
}) {
  return (
    <tr className="border-t border-neutral-100 align-top transition-colors hover:bg-neutral-50/60">
      <td className="px-3 py-2">
        <Link
          to="/tex/cases/$caseId"
          params={{ caseId: item.case_id }}
          search={{}}
          className="font-medium text-tex-blue hover:underline"
        >
          <Mono>{item.reference}</Mono>
        </Link>
        {item.specialist_review_required && (
          <div className="text-xs text-amber-800">
            Specialist review required
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        <div>{item.buyer_business_name}</div>
        <div className="text-xs text-neutral-500">{item.dealer_legal_name}</div>
      </td>
      <td className="px-3 py-2 text-right">
        <Mono>{formatAmount(item.requested_principal)}</Mono>
      </td>
      <td className="px-3 py-2 text-xs text-neutral-500">
        {formatDate(item.stage_changed_at)}
        {item.submitted_at && (
          <div className="text-neutral-400">
            submitted {formatDate(item.submitted_at)}
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        <AssignControl
          me={me}
          caseId={item.case_id}
          assigned={item.assigned_reviewer}
          reviewers={reviewers}
        />
      </td>
      <td className="px-3 py-2 text-xs text-neutral-500">
        {item.review ? (
          <>
            <div>
              v{item.review.version} ·{" "}
              {REVIEW_STATUS_LABELS[item.review.status]}
              {item.review.frozen_at && " · frozen"}
            </div>
            <div className="text-neutral-400">
              {item.unresolved_blocking_flags > 0 &&
                `${item.unresolved_blocking_flags} blocking · `}
              {item.open_requests > 0 && `${item.open_requests} open · `}
              {item.answered_requests > 0 &&
                `${item.answered_requests} answered`}
            </div>
          </>
        ) : (
          <span className="text-neutral-400">{humanize("not_started")}</span>
        )}
      </td>
      <td className="px-3 py-2">
        <NextActionBadge action={item.next_action} />
      </td>
    </tr>
  );
}
